import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/api";
import { TrendingUp, TrendingDown, Wallet, CalendarClock, ArrowUpRight, ArrowDownRight, Globe, Coins } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";

const Stat = ({ label, value, tone, icon: Icon, hint }) => (
  <div className={`relative overflow-hidden rounded-2xl p-6 border border-slate-200 bg-white`}>
    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 ${tone.bg}`} />
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</span>
      <div className={`w-9 h-9 rounded-full grid place-items-center ${tone.bg}`}>
        <Icon className={`w-4 h-4 ${tone.text}`} />
      </div>
    </div>
    <div className="mt-4 font-mono-num text-3xl font-bold text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { currency } = useCurrency();
  const [sum, setSum] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [txs, setTxs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [chqUpcoming, setChqUpcoming] = useState(null);
  const [kasaCons, setKasaCons] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, t, b, u, c, chq, kc] = await Promise.all([
        api.get(`/reports/summary?currency=${currency}`),
        api.get("/transactions"),
        api.get("/bank-accounts"),
        api.get("/upcoming"),
        api.get("/reports/consolidated"),
        api.get("/cheques/upcoming"),
        api.get("/kasa/consolidated").catch(() => ({ data: null })),
      ]);
      setSum(s.data);
      setTxs(t.data.filter((x) => x.currency === currency).slice(0, 6));
      setBanks(b.data.filter((x) => x.currency === currency));
      setUpcoming(u.data.filter((x) => x.currency === currency && x.status !== "paid").slice(0, 5));
      setConsolidated(c.data);
      setChqUpcoming(chq.data);
      setKasaCons(kc.data);
    })();
  }, [currency]);

  const curSym = (c) => (c === "TRY" ? "₺" : c === "USD" ? "$" : c === "EUR" ? "€" : c);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Genel Bakış</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Finansal Panel</h1>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Bugün</div>
          <div className="font-mono-num text-slate-700">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat label="Toplam Gelir" value={formatMoney(sum?.income_total || 0, currency)} tone={{ bg: "bg-emerald-100", text: "text-emerald-600" }} icon={ArrowUpRight} hint={`${currency} · Tüm zamanlar`} />
        <Stat label="Toplam Gider" value={formatMoney(sum?.expense_total || 0, currency)} tone={{ bg: "bg-rose-100", text: "text-rose-600" }} icon={ArrowDownRight} hint={`${currency} · Tüm zamanlar`} />
        <Stat label="Net Kar/Zarar" value={formatMoney(sum?.profit || 0, currency)} tone={{ bg: "bg-blue-100", text: "text-blue-600" }} icon={TrendingUp} hint="Gelir − Gider" />
        <Stat label="Banka Toplam" value={formatMoney(sum?.bank_total || 0, currency)} tone={{ bg: "bg-cyan-100", text: "text-cyan-600" }} icon={Wallet} hint={`${banks.length} hesap`} />
      </div>

      {consolidated && (
        <div data-testid="net-worth-card" className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-[#1E2A9C] via-[#1E2A9C] to-[#29B7E3] text-white">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-cyan-300/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 grid place-items-center backdrop-blur">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-cyan-100 font-bold">Konsolide · TCMB Kuruyla</div>
                  <div className="font-display font-extrabold text-2xl">Toplam Net Varlık</div>
                </div>
              </div>
              <div className="text-right text-[11px] uppercase tracking-widest text-cyan-100 font-bold">
                USD/TRY {consolidated.rates?.USD?.toFixed(2)} · EUR/TRY {consolidated.rates?.EUR?.toFixed(2)}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-cyan-100 font-bold">Toplam Varlık (TRY)</div>
                <div className="font-mono-num text-5xl font-extrabold mt-1">₺{consolidated.totals_try.net_worth.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-cyan-100 mt-2">Banka + Tahsilat − Ödeme (tüm para birimleri toplamı)</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-widest text-cyan-100 font-bold">Banka</div>
                <div className="font-mono-num text-lg font-bold">₺{consolidated.totals_try.bank.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-widest text-cyan-100 font-bold">Kar/Zarar</div>
                <div className={`font-mono-num text-lg font-bold ${consolidated.totals_try.profit >= 0 ? "text-emerald-200" : "text-rose-200"}`}>
                  ₺{consolidated.totals_try.profit.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {["TRY", "USD", "EUR"].map(c => (
                <div key={c} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-100 font-bold">{c} Bakiye</div>
                  <div className="font-mono-num text-lg font-bold">
                    {c === "TRY" ? "₺" : c === "USD" ? "$" : "€"}{consolidated.by_currency[c].bank.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-[10px] text-cyan-200 mt-0.5 font-mono-num">= ₺{consolidated.by_currency[c].bank_in_try.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {chqUpcoming && chqUpcoming.count > 0 && (
        <div data-testid="cheque-widget" className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl fade-up">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold flex items-center gap-2">
                <CalendarClock className="w-3 h-3" /> 7 Gün İçinde Vadesi Gelecek Çekler
              </div>
              <div className="font-display font-extrabold text-xl text-amber-900 dark:text-amber-100">{chqUpcoming.count} Çek Vadesinde</div>
            </div>
            <Link to="/cek" className="text-xs text-amber-700 hover:underline font-bold">Tümü →</Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-emerald-100">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Tahsil Edilecek</div>
              <div className="font-mono-num text-xl font-bold text-emerald-700">{formatMoney(chqUpcoming.received_total, "TRY")}</div>
            </div>
            <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-rose-100">
              <div className="text-[10px] uppercase tracking-widest text-rose-600 font-bold">Ödenecek</div>
              <div className="font-mono-num text-xl font-bold text-rose-700">{formatMoney(chqUpcoming.issued_total, "TRY")}</div>
            </div>
          </div>
        </div>
      )}

      {kasaCons && kasaCons.total_kasa > 0 && (
        <div data-testid="kasa-consolidated-widget" className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white fade-up">
          <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 grid place-items-center backdrop-blur">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-amber-50 font-bold">Konsolide Kasa · TCMB Kuruyla</div>
                  <div className="font-display font-extrabold text-2xl">Fiziksel Nakit Toplamı</div>
                </div>
              </div>
              <div className="text-right">
                <Link to="/kasa" className="text-xs text-amber-50 hover:underline font-bold">Kasa Yönet →</Link>
                <div className="text-[10px] uppercase tracking-widest text-amber-100 font-bold mt-1">
                  Kur: {kasaCons.rate_date || "-"}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-amber-100 font-bold">TL Karşılığı Toplam</div>
                <div className="font-mono-num text-4xl font-extrabold mt-1">₺{kasaCons.total_try.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-amber-100 mt-2">{kasaCons.total_kasa} kasa · {Object.values(kasaCons.by_currency).filter(c => c.count > 0).length} para birimi</div>
              </div>
              {["TRY", "USD", "EUR"].map(c => {
                const agg = kasaCons.by_currency[c];
                if (!agg || agg.count === 0) return null;
                return (
                  <div key={c} className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-amber-100 font-bold">{c} Nakit</div>
                      <div className="text-[10px] text-amber-100 font-mono-num">{agg.count} kasa</div>
                    </div>
                    <div className="font-mono-num text-xl font-bold mt-1">
                      {curSym(c)}{agg.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {c !== "TRY" && (
                      <div className="text-[10px] text-amber-100 mt-0.5 font-mono-num">= ₺{agg.in_try.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} <span className="opacity-70">· {agg.rate.toFixed(2)}</span></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Nakit Akışı</div>
              <div className="font-display text-xl font-bold text-slate-900">Aylık Gelir & Gider Trendi</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={sum?.monthly || []}>
                <defs>
                  <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="ge" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fill="url(#gi)" />
                <Area type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} fill="url(#ge)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Yaklaşan</div>
              <div className="font-display text-xl font-bold text-slate-900">Ödeme & Tahsilat</div>
            </div>
            <Link to="/odemeler" className="text-xs text-[#1E2A9C] hover:underline">Tümü</Link>
          </div>
          <div className="space-y-3">
            {upcoming.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">Kayıt yok</div>}
            {upcoming.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{u.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <CalendarClock className="w-3 h-3" />
                    {formatDate(u.due_date)}
                    {u.status === "overdue" && <span className="text-red-600 font-bold">· Vadesi Geçmiş</span>}
                  </div>
                </div>
                <div className={`font-mono-num text-sm font-bold ${u.type === "collection" ? "text-emerald-600" : "text-rose-600"}`}>
                  {u.type === "collection" ? "+" : "−"}{formatMoney(u.amount, u.currency)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Son İşlemler</div>
              <div className="font-display text-xl font-bold text-slate-900">Hareketler</div>
            </div>
          </div>
          <div className="space-y-2">
            {txs.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">Henüz işlem yok</div>}
            {txs.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full grid place-items-center ${t.type === "income" ? "bg-emerald-100" : "bg-rose-100"}`}>
                    {t.type === "income" ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{t.description || t.category}</div>
                    <div className="text-xs text-slate-500">{t.category} · {formatDate(t.date)}</div>
                  </div>
                </div>
                <div className={`font-mono-num font-bold ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                  {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Banka Hesapları</div>
              <div className="font-display text-xl font-bold text-slate-900">{currency} Bakiyeler</div>
            </div>
            <Link to="/banka" className="text-xs text-[#1E2A9C] hover:underline">Tümü</Link>
          </div>
          <div className="space-y-3">
            {banks.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">Banka hesabı eklenmedi</div>}
            {banks.map((b) => (
              <div key={b.id} className="p-4 rounded-xl border border-slate-200 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.color}15, ${b.color}05)` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{b.bank_name}</div>
                    <div className="text-xs text-slate-500">{b.name}</div>
                  </div>
                  <div className="font-mono-num text-xl font-bold" style={{ color: b.color }}>
                    {formatMoney(b.balance, b.currency)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
